import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, NgModule, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { FormService } from '../../services/form.service';

import { MessageService } from 'primeng/api';
import { BaseFormComponent } from '../../models/base/base-form.component';
import { FormControl, Validators, FormGroup, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LookupService } from '../../services/lookup.service';
import { FormHelperService } from '../../services/form-helper.service';
import { CommonModule } from '@angular/common';
import { DynamicFormModule } from '../shared/controls/dynamic/dynamic-form.component';
import { NotesModule } from '../shared/controls/notes.component';
import { DialogModule } from 'primeng/dialog';
import { LoadingModule } from '../shared/controls/loading.component';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { ContactsDetailModule } from '../shared/controls/contacts-detail.component';
import { ContactsFormModule } from '../shared/controls/contacts-form.component';
import { DynamicValueModule } from '../shared/controls/dynamic/dynamic-value.component';
import { KebobMenuModule } from '../shared/controls/dynamic/kebob-menu.component';
import { SimpleControlModule } from '../shared/controls/dynamic/simple-control.component';
import { LastUpdatedModule } from '../shared/controls/last-updated.component';
import { TableModule } from 'primeng/table';
import { OrderFormModule } from '../orders/order-form.component';
import { PaginatorCustomModule } from '../shared/controls/dynamic/paginator-custom.component';
import { SortIconCustomModule } from '../shared/controls/dynamic/sort-icon-custom.component';
import { NotesDetailModule } from '../shared/controls/notes-detail.component';
import { RowToolsModule } from '../shared/controls/row-tools.component';
import { UserFormModule } from '../users/user-form.component';
import { LocationService } from '../../services/location.service';
import { NavigationService } from '../../services/navigation.service';
import { SharedService } from '../../services/shared.service';
import { NoteService } from '../../services/note.service';
import { Note } from '../../models/note.model';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, forkJoin, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'location-form',
  templateUrl: './location-form.component.html',
  providers: [FormService, LookupService, LocationService, NoteService],
  changeDetection: ChangeDetectionStrategy.Default
})
export class LocationFormComponent extends BaseFormComponent implements OnInit,OnDestroy {
  @Input() id: string;
  @Output() onCancel: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSaveComplete: EventEmitter<any> = new EventEmitter<any>();
  @Output() onLoad: EventEmitter<any> = new EventEmitter<any>();

  isLoading: boolean = false;
  isEdit: boolean = false;
  serverError: string = null;
  metadata: any = {};
  errors: string[] = [];
  newNotes: any[] = [];
  private destroy$ = new Subject<void>();
  
  get formInvalid(): boolean {
    return this.isLoading || !this.form?.valid || this.errors?.length > 0;
  }
  
  constructor(
    protected messageService: MessageService,
    protected fh: FormHelperService,
    private formService: FormService,
    private router: Router,
    private lookupService: LookupService,
    private navigationService: NavigationService,
    public sharedService: SharedService,
    private locationService: LocationService,
    private noteService: NoteService,
    private ref: ChangeDetectorRef) {
    super();
    this.entityName = 'location';
  }

  ngOnInit() {
    this.isAdding = this.id == null || this.id == '';
    this.isEditing = this.id != null && this.id != '';

    if(this.isAdding){
      this.isEdit = true
    }
    this.isLoading = true;
    this.loadLookup('stateCode');
    this.load();
  }

  loadForm() {
    this.form = new FormGroup({}, []);

    this.form.addControl('id', new FormControl(this.model.id));

    this.form.addControl('businessName', new FormControl(this.model.businessName, [Validators.required]));
    this.form.addControl('businessHours', new FormControl(this.model.businessHours));
    this.form.addControl('phone', new FormControl(this.model.phone, [Validators.required, Validators.minLength(10), Validators.maxLength(18)]));
    this.form.addControl('address1', new FormControl(this.model.address1, [Validators.required]));
    this.form.addControl('stateCode', new FormControl(this.model.stateCode, [Validators.required]));
    this.form.addControl('city', new FormControl(this.model.city, [Validators.required]));
    this.form.addControl('zip', new FormControl(this.model.zip, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]));
    this.form.addControl('code', new FormControl(this.model.code));
    this.form.addControl('pickupNotes', new FormControl(this.model?.locationPickupNotes));
    this.form.addControl('deliveryNotes', new FormControl(this.model?.locationDeliveryNotes));

    if (!this.isAdding) {
      if (this.model.contacts != null && this.model.contacts.length > 0) {
        let forms = [];
        this.model.contacts.forEach(c => {
          forms.push(this.fh.getContactForm(c, true, true));
        });

        this.form.addControl('contacts', new FormArray(forms));
      }
      else {
        this.form.addControl('contacts', new FormArray([]));
      }
    }
    else {
      this.form.addControl('contacts', new FormArray([]));
    }

  }

  loadLookup(key: string, force: boolean = false) {
    this.lookupService
      .get(key, force)
      .pipe(takeUntil(this.destroy$))
      .subscribe((r) => {
        this.lookups[key] = r;
      });
  }

  load() {
    if (this.isAdding) {
      this.formService
        .getEditNew(this.entityName)
        .pipe(takeUntil(this.destroy$))
        .subscribe((r) => {
          this.model = {};
          this.metadata = r.body;
          this.loadForm();
          this.ref.markForCheck();
          this.isLoading = false;
        });
    }
    else if (this.isEditing) {
      this.formService
        .getEdit(this.entityName, this.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((r) => {
          this.model = r.body;
          this.metadata = this.model.metadata;
          this.loadForm();
          this.ref.markForCheck();
          this.isLoading = false;

          this.onLoad.emit(this.model);
        });
    }
  }

  async save() {
    let f = this.form.value;
    this.errors = [];
    let valid = true;
    try {
      await this.checkLocation();
    } catch (err) {
      if (err) {
        valid = false;
      }
    }

    this.form.updateValueAndValidity();
    if (!this.form.valid) {
      valid = false;
      this.form.markAllAsTouched();
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsDirty();
      });
    }

    if (!valid) {
      this.ref.markForCheck();
      return;
    }

    if (!this.isLoading) {
      if (this.isAdding) {
        this.isLoading = true;
        this.formService
          .add(this.entityName, f)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (res) => {
              if (res != null && !res.isError) {
                const locationId = res.body;

                const finishSave = () => {
                  this.sharedService
                    .getMenuCount("tenderslocations")
                    .subscribe((menuRes) => {
                      this.navigationService.menuCountChange(
                        menuRes.body,
                        "tenderslocations"
                      );
                    });
                  this.messageService.add(
                    this.toast(true, "Location added successfully")
                  );
                  this.router.navigate(["/locations"]);
                  this.ref.markForCheck();
                  this.isLoading = false;
                  this.onSaveComplete.emit(res.body);
                };

                if (this.newNotes && this.newNotes.length > 0) {
                  const noteObservables = this.newNotes.map(note => {
                    const n = new Note();
                    n.message = note.message;
                    n.action = note.action;
                    n.entityName = 'location';
                    n.entityId = locationId;
                    return this.noteService.add(n);
                  });
                  forkJoin(noteObservables)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(
                      () => finishSave(),
                      () => finishSave()
                    );
                } else {
                  finishSave();
                }
              }
            },
            (err) => {
              this.ref.markForCheck();
              this.isLoading = false;

              if (err.error.message != null) {
                this.messageService.add(this.toast(false, err.error.message));
                this.serverError = err.error.message;
              } else if (err.status == 500) {
                this.messageService.add(
                  this.toast(false, "Internal Server Error")
                );
                this.serverError = "Internal Server Error";
              }
            }
          );
      }
      else if (this.isEditing) {
        this.isLoading = true;
        this.formService
          .edit(this.entityName, f)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (res) => {
              if (res != null && !res.isError) {
                this.sharedService
                  .getMenuCount("tenderslocations")
                  .subscribe((res) => {
                    this.navigationService.menuCountChange(
                      res.body,
                      "tenderslocations"
                    );
                  });
                this.messageService.add(
                  this.toast(true, "Location updated successfully")
                );
                this.router.navigate(["/locations"]);
                this.ref.markForCheck();
                this.isLoading = false;
                this.onSaveComplete.emit(res.body);
              }
            },
            (err) => {
              this.ref.markForCheck();
              this.isLoading = false;

              if (err.error.message != null) {
                this.messageService.add(this.toast(false, err.error.message));
                this.serverError = err.error.message;
              } else if (err.status == 500) {
                this.messageService.add(
                  this.toast(false, "Internal Server Error")
                );
                this.serverError = "Internal Server Error";
              }
            }
          );
      }
    }
  }

  checkLocation() {
    return new Promise((resolve, reject) => {
      let name = this.form.get('businessName').value;
      let city = this.form.get('city').value;
      let zip = this.form.get('zip').value;
      const locationId = this.form.get('id').value || null;
      this.locationService
        .locationExists(name, city, zip, locationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (r) => {
            return resolve(true);
          },
          (err) => {
            Sentry.captureException(err);
            if (err?.error?.isError) {
              const errorMessage = err?.error?.message;
              if (errorMessage && !this.errors.includes(errorMessage)) {
                this.errors.push(errorMessage);
              }
            }
            return reject(true);
          }
        );
    })
  }

  cancel() {
    this.form = new FormGroup({}, []);
    this.ref.markForCheck();
    this.onCancel.emit(null);
  }

  editLocation(){
    this.isEdit = !this.isEdit
  }

  get title(): string {
    let t='';
    if( !this.isLoading){
      t='Location'
    }
    if(this.form != null && this.form.contains('businessName') && this.form.get('businessName').value != null && this.form.get('businessName').value.length > 0 && !this.isLoading) {
      t= this.form.get('businessName').value || 'Location';
    }
    if (this.isAdding && !this.isLoading) {
      t = t + "(New)";
    } else if (this.isEditing && !this.isLoading && this.isEdit) {
      t = t + "(Edit)";
    } else if (this.isEditing && !this.isLoading && !this.isEdit) {
      t = t + "(View)";
    }
    return t;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

@NgModule({
  declarations: [
    LocationFormComponent,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    DynamicFormModule,
    SimpleControlModule,
    NotesModule,
    DialogModule,
    LoadingModule,
    DropdownModule,
    FormsModule,
    ButtonModule,
    LastUpdatedModule,
    SortIconCustomModule,
    PaginatorCustomModule,
    DynamicValueModule,
    KebobMenuModule,
    UserFormModule,
    OrderFormModule,
    RowToolsModule,
    NotesDetailModule,
    ContactsFormModule,
    ContactsDetailModule,
  ],
  exports: [
    LocationFormComponent
  ]
})
export class LocationFormModule { }
